// Error boundary — zachyti runtime errors v komponentach a zobrazi
// debug fallback misto white/black screen.
//
// Logy se uchovaji v localStorage pro vzdaleny support (uzivatel posle screenshot
// nebo dump z developer console).

import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("[ErrorBoundary]", error, errorInfo);
    this.setState({ errorInfo });
    // Persistuj posledni error do localStorage pro support diagnostiku
    try {
      localStorage.setItem(
        "routera.lastError",
        JSON.stringify({
          message: error.message,
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          ts: new Date().toISOString(),
          ua: navigator.userAgent,
        }),
      );
    } catch {
      /* ignore quota errors */
    }
  }

  reset = () => {
    this.setState({ error: null, errorInfo: null });
  };

  reload = () => {
    window.location.reload();
  };

  clearAndReload = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            padding: 20,
            background: "rgb(244 240 232)",
            color: "rgb(30 34 44)",
            fontFamily: "Inter, system-ui, sans-serif",
            minHeight: "100vh",
            overflow: "auto",
          }}
        >
          <h1 style={{ fontSize: 20, color: "rgb(200 35 35)", marginBottom: 12 }}>
            ⚠️ Chyba v aplikaci
          </h1>
          <p style={{ fontSize: 14, marginBottom: 16, color: "rgb(89 97 112)" }}>
            Vyskytla se neočekávaná chyba. Můžeš zkusit obnovit obrazovku, nebo úplně
            vymazat data appky (pokud reload nepomůže).
          </p>

          <div
            style={{
              background: "rgb(252 249 243)",
              border: "1px solid rgb(222 213 196)",
              borderRadius: 8,
              padding: 12,
              fontSize: 12,
              fontFamily: "ui-monospace, monospace",
              marginBottom: 16,
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
            }}
          >
            <strong style={{ display: "block", marginBottom: 6 }}>
              {this.state.error.name}: {this.state.error.message}
            </strong>
            {this.state.error.stack && (
              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: "pointer", color: "rgb(89 97 112)" }}>
                  Stack trace
                </summary>
                <div style={{ marginTop: 6, fontSize: 11, opacity: 0.85 }}>
                  {this.state.error.stack}
                </div>
              </details>
            )}
            {this.state.errorInfo?.componentStack && (
              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: "pointer", color: "rgb(89 97 112)" }}>
                  Component stack
                </summary>
                <div style={{ marginTop: 6, fontSize: 11, opacity: 0.85 }}>
                  {this.state.errorInfo.componentStack}
                </div>
              </details>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button
              onClick={this.reload}
              style={{
                padding: "12px 16px",
                background: "rgb(37 99 235)",
                color: "#fff",
                border: 0,
                borderRadius: 8,
                fontSize: 15,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              ↻ Obnovit stránku
            </button>
            <button
              onClick={this.clearAndReload}
              style={{
                padding: "12px 16px",
                background: "transparent",
                color: "rgb(200 35 35)",
                border: "1px solid rgb(200 35 35)",
                borderRadius: 8,
                fontSize: 14,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              🗑 Vymazat data + obnovit
            </button>
          </div>

          <p
            style={{
              fontSize: 11,
              color: "rgb(89 97 112)",
              marginTop: 24,
              textAlign: "center",
            }}
          >
            Detail chyby uložen v localStorage jako `routera.lastError`.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}
